"use client"

import { HStack, Text } from "@chakra-ui/react"
import { useRouter } from "next/navigation"
import { FC, useState } from "react"

import LikeButton from "@/components/LikeButton/LikeButton"
import { Toaster, toaster } from "@/components/ui"
import { useAuth } from "@/contexts/AuthContext"
import { redirectToSignin } from "@/lib/auth-utils"

import PostCardCommentBtn from "./PostCardCommentBtn"
import ShareDropdown from "./ShareDropdown"

interface Props {
  likeCount: number
  liked: boolean
  commentCount: number
  articleSlug: string
  title?: string
  allow_comments?: boolean | null | undefined
  author?: string
  className?: string
  readingTime: string
  articlePathPrefix?: "/article" | "/case-study"
}

const ArticleMetaActions: FC<Props> = ({
  allow_comments = true,
  articlePathPrefix = "/article",
  articleSlug = "",
  className = "article-meta-actions",
  commentCount = 0,
  likeCount: initialLikeCount = 0,
  liked: initialLiked = false,
  readingTime = undefined,
}) => {
  const { session, status } = useAuth()
  const router = useRouter()
  const [likeStateOverride, setLikeStateOverride] = useState<{
    count: number
    liked: boolean
  } | null>(null)
  const [loading, setLoading] = useState(false)
  const likeCount = likeStateOverride?.count ?? initialLikeCount
  const liked = likeStateOverride?.liked ?? initialLiked

  const handleLike = async () => {
    if (status === "loading") return

    if (!session) {
      toaster.create({
        action: {
          label: "Sign In",
          onClick: () => redirectToSignin(router),
        },
        closable: true,
        description: "You must sign in to like articles",
        title: "Sign in required",
        type: "info",
      })
      return
    }

    setLoading(true)

    try {
      const response = await fetch(`/api/articles/${articleSlug}/like`, {
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
      })

      const data = await response.json()

      if (data.success) {
        setLikeStateOverride({
          count: data.likes,
          liked: data.has_liked,
        })
      } else {
        console.error("Failed to like article:", data.message)
      }
    } catch (error) {
      console.error("Error liking article:", error)
    } finally {
      setLoading(false)
    }
  }

  const articleReadingTime = readingTime?.trim()
  const hasArticleReadingTime = Boolean(
    articleReadingTime && articleReadingTime !== "0"
  )

  return (
    <>
      <Toaster />

      <HStack
        alignItems="stretch"
        className={className}
        flexShrink={0}
        minH={"64px"}
      >
        <HStack
          alignItems={"stretch"}
          borderTop={{
            base: "1px solid {colors.border}",
            lg: "1px solid transparent",
          }}
          flexGrow={1}
          gap={0}
          h={"full"}
          justifyContent="flex-end"
          minH={"64px"}
        >
          {/* Reading Time */}
          {hasArticleReadingTime && (
            <HStack
              borderLeft={{
                base: "1px solid transparent",
                lg: "1px solid {colors.border}",
              }}
              borderRight={"1px solid {colors.border}"}
              color="fg.muted"
              flexGrow={{ base: 1, lg: 0 }}
              flexShrink={0}
              fontSize="sm"
              minH={"64px"}
              my={{ base: 0, md: 0 }}
              px={4}
            >
              <svg
                aria-label="Reading time"
                fill="currentColor"
                height="24"
                viewBox="0 0 24 24"
                width="24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <title>Reading time</title>
                <path d="M2.5 3.5a.75.75 0 0 0 0 1.5h18a.75.75 0 0 0 0-1.5z" />
                <path
                  clipRule="evenodd"
                  d="M16 10.25a4 4 0 1 0 2.828 6.828a.75.75 0 0 0 0-1.06l-2.078-2.079V11a.75.75 0 0 0-.75-.75m-1.178 1.795q.206-.11.428-.18v2.385c0 .199.079.39.22.53l1.686 1.687a2.5 2.5 0 0 1-1.644.235a2.5 2.5 0 0 1-.69-4.657"
                  fillRule="evenodd"
                />
                <path
                  clipRule="evenodd"
                  d="M16 8a6.25 6.25 0 1 0 0 12.5A6.25 6.25 0 0 0 16 8m-4.75 6.25a4.75 4.75 0 1 1 9.5 0a4.75 4.75 0 0 1-9.5 0"
                  fillRule="evenodd"
                />
                <path d="M2.5 8.5a.75.75 0 0 0 0 1.5H8a.75.75 0 0 0 0-1.5zm0 5a.75.75 0 0 0 0 1.5H8a.75.75 0 0 0 0-1.5zm0 5a.75.75 0 0 0 0 1.5H8a.75.75 0 0 0 0-1.5z" />
              </svg>
              <Text
                alignItems="flex-end"
                aria-label={`${articleReadingTime} minute read`}
                as="p"
                display="inline-flex"
                fontFamily="mono"
                fontSize="sm"
                gap={1}
              >
                <Text as="span" fontWeight={600}>
                  {articleReadingTime ? `${articleReadingTime} ` : ""}
                </Text>
                min.
              </Text>
            </HStack>
          )}
          {allow_comments && (
            <LikeButton
              disabled={status === "loading"}
              handleLike={handleLike}
              isLiked={liked}
              likeCount={likeCount.toString()}
              loading={loading}
            />
          )}
          {allow_comments && (
            <PostCardCommentBtn
              articlePathPrefix={articlePathPrefix}
              articleSlug={articleSlug}
              commentCount={commentCount}
            />
          )}
          <ShareDropdown
            articlePathPrefix={articlePathPrefix}
            handle={articleSlug}
          />
        </HStack>
      </HStack>
    </>
  )
}

export default ArticleMetaActions
